import {Col, Form, Input, Radio, Row, Select, Switch} from 'antd';
import {useEffect, useState} from 'react';
import {useMutation} from '@apollo/client';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {UPSERT_NOTIFICATION_WORKFLOW} from 'graphql/mutations/notificationOrchestrator';
import styles from './workflowOrchestrator.module.scss';

const PRIORITY_OPTIONS = [
  {
    value: 'STANDARD',
    label: 'Standard',
    copy: 'Normal — follows all bucket rules.',
  },
  {
    value: 'HIGH',
    label: 'High',
    copy: 'Important — will always be delivered. May be delayed but never dropped.',
  },
  {
    value: 'CRITICAL',
    label: 'Critical',
    copy: 'Critical — bypasses all rate limits. Admin only — sends ignoring cooldowns.',
  },
];

const WorkflowForm = ({workflow, buckets, onClose, onSaved}) => {
  const [form] = Form.useForm();
  const isEdit = !!workflow;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  const priority = Form.useWatch('priority', form);

  useEffect(() => {
    if (workflow) {
      form.setFieldsValue({
        workflowKey: workflow.workflowKey,
        name: workflow.name,
        description: workflow.description,
        bucket: workflow.bucket,
        priority: workflow.priority || 'STANDARD',
        active: workflow.active ?? true,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        priority: 'STANDARD',
        active: true,
      });
    }
  }, [workflow, form]);

  const [upsertWorkflow, {loading}] = useMutation(
    UPSERT_NOTIFICATION_WORKFLOW,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const submit = () => {
    if (!pendingValues) return;
    const values = pendingValues;
    const input = {
      workflowKey: values.workflowKey?.trim(),
      name: values.name?.trim(),
      bucket: values.bucket,
      priority: values.priority,
      active: !!values.active,
    };

    const description = values.description?.trim();
    if (description) {
      input.description = description;
    }

    upsertWorkflow({variables: {input}}).then(({data}) => {
      if (!data) return;
      const result = data.upsertNotificationWorkflow;
      if (result.__typename === 'Error') {
        toast.error(result.message);
      } else {
        toast.success(
          result.message || (isEdit ? 'Workflow updated' : 'Workflow created'),
        );
        setConfirmOpen(false);
        setPendingValues(null);
        onSaved?.();
        onClose?.();
      }
    });
  };

  const handleFinish = values => {
    setPendingValues(values);
    setConfirmOpen(true);
  };

  const activeBuckets = (buckets || []).filter(b => b.active);

  return (
    <div className={styles.formWrap}>
      <div className={styles.modalHead}>
        <h3 className={styles.modalHeader}>
          {isEdit ? 'Edit Workflow' : 'Create Workflow'}
        </h3>
        <p className={styles.modalSub}>
          A workflow is a single trigger. It declares which bucket it belongs to
          and how urgent it is.
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        className={styles.modalForm}
        onFinish={handleFinish}
        scrollToFirstError>
        <div className={styles.formSection}>
          <h4 className={styles.sectionHeading}>Identity</h4>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="workflowKey"
                label="Workflow key"
                tooltip="Must match the producer's identifier. Unique and immutable."
                rules={[{required: true, message: 'Workflow key is required'}]}>
                <Input
                  placeholder="e.g. HAS_MADE_DEPOSIT_GET_PRO"
                  disabled={isEdit}
                  className={styles.input}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Display name"
                rules={[{required: true, message: 'Name is required'}]}>
                <Input placeholder="Friendly name" className={styles.input} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <Input.TextArea
              rows={2}
              placeholder="One-line description"
              className={styles.input}
            />
          </Form.Item>
        </div>

        <div className={styles.formSection}>
          <h4 className={styles.sectionHeading}>Routing</h4>
          <Form.Item
            name="bucket"
            label="Bucket"
            tooltip="Rate-limit and approval rules come from the bucket."
            rules={[{required: true, message: 'Bucket is required'}]}>
            <Select placeholder="Pick a bucket" showSearch>
              {activeBuckets.map(b => (
                <Select.Option key={b.key} value={b.key}>
                  {b.name} ({b.key})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="priority"
            label="Priority"
            rules={[{required: true, message: 'Priority is required'}]}>
            <Radio.Group className={styles.radioStack}>
              {PRIORITY_OPTIONS.map(opt => (
                <Radio key={opt.value} value={opt.value}>
                  <span className={styles.radioLabel}>{opt.label}</span>
                  <span className={styles.radioHelper}>{opt.copy}</span>
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>

          {priority === 'CRITICAL' && (
            <div className={styles.warningBox}>
              CRITICAL workflows bypass the gatekeeper entirely. Use only for
              security, account or regulatory messages.
            </div>
          )}
        </div>

        <div className={styles.formSection}>
          <div className={styles.inlineRow}>
            <div>
              <div className={styles.inlineLabel}>Active</div>
              <div className={styles.inlineHelper}>
                Inactive workflows are still gated but can be disabled.
              </div>
            </div>
            <Form.Item
              name="active"
              valuePropName="checked"
              style={{marginBottom: 0}}>
              <Switch />
            </Form.Item>
          </div>
        </div>

        <div className={styles.formActions}>
          <SubmitButton
            label={isEdit ? 'Save Changes' : 'Create Workflow'}
            disabled={loading}
          />
        </div>
      </Form>

      <ConfirmModal
        title={
          pendingValues?.priority === 'CRITICAL'
            ? 'Confirm CRITICAL priority'
            : isEdit
            ? 'Save Workflow Changes'
            : 'Create Workflow'
        }
        openModal={confirmOpen}
        setOpenModal={setConfirmOpen}
        loading={loading}
        handleOperation={submit}
        confirmText={
          pendingValues?.priority === 'CRITICAL' ? (
            <p className={styles.confirmText}>
              <strong>Are you sure?</strong> This workflow will ignore cooldowns
              and rate-limits and will send immediately to every targeted user.
              Use only for security, account or regulatory messages.
            </p>
          ) : (
            <p className={styles.confirmText}>
              {isEdit
                ? `You're updating workflow "${
                    pendingValues?.name || pendingValues?.workflowKey || ''
                  }". It will route through bucket "${
                    pendingValues?.bucket || ''
                  }" with priority ${pendingValues?.priority || 'STANDARD'}.`
                : `You're creating workflow "${
                    pendingValues?.name || pendingValues?.workflowKey || ''
                  }". It will be gated by bucket "${
                    pendingValues?.bucket || ''
                  }" with priority ${pendingValues?.priority || 'STANDARD'}.`}
            </p>
          )
        }
      />
    </div>
  );
};

export default WorkflowForm;
